import { Component, OnInit, Input, Output, EventEmitter, Optional, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TaxReturnService } from '../../../../../swaggerServices/api/api';
import { ITaxReturn } from '../../../../app/tax-returns/tax-return/model/i-tax-return';
import { ConfigStateService } from '../../../../app/config/config-state.service';
import { TaxReturnStatus } from '../../../../app/tax-returns/tax-return/model/status';
import { PutFinalPackageBody } from '../../../../../swaggerServices';
import { finalize } from 'rxjs/operators';
import { FinalPackagePreviewUI } from '../tax-return-actions';
import { TenantConfigService } from '../../../services/tenant-config.service';
import { Tenant } from "../../../../shared/types/tenant.enum";
import { Subscription } from 'rxjs';
import { BaseURLService } from '../../../../shared/util/baseUrl.service';


@Component({
    selector: 'preview-final-package',
    templateUrl: './preview-final-package.component.html',
    styleUrls: ['./preview-final-package.component.scss'],
    standalone: false
})
export class PreviewFinalPackageComponent implements OnInit {
  taxReturn: ITaxReturn;
  @Output() closed = new EventEmitter();
  @Output() completed = new EventEmitter();
  finalPackagePreview: FinalPackagePreviewUI = { documents: [], skipEmail: false, skipLink: false };
  canSendToTaxpayer = true;
  sendConfirmationEmail = this.config.featureFlags.defaultSendLinkConfirmationEmail && !(this.config.featureFlags.selectSkipEmailAndUnselectLinkConfirmation);
  modalState: 'loading' | 'form' | 'success' | 'error' = 'form';
  successMsg = '';
  linkUploadRetry = false;
  isLoading = false;
  modalSize = 'medium';
  columnVisible = {
    sendToTaxpayer: false,
    sendToEmployer: false,
    sendToTaxOffice: false,
    fileSize: false,
    use: true,
    pageCount: false,
    lastUpdatedBy: true,
  };
  taxOfficeName;
  isDocshare = false;
  MAX_PACKAGE_SIZE = 10 * 1024 * 1024; // max 10MB to be sent via LINK
  maxPackageSizeExceeded = false;
  maxEncryptedPackageSizeExceeded = false;
  currentTaxpayerPackageSize = 0;
  taxpayerPackageEmpty = false;
  employerPackageEmpty = false;
  taxAuthorityPackageEmpty = false;
  taxPageTotal = 0;
  subscriptions: Subscription[] = [];
  taxpayerAlias: string;

  uploadedToLink = true;
  showSkipLink = this.config.featureFlags.defaultSendLinkConfirmationEmail;
  showSkipLinkEmail = this.config.featureFlags.defaultSendLinkConfirmationEmail;
  private docshareId;

  private dialogData: { taxReturn: ITaxReturn } = inject(MAT_DIALOG_DATA);

  constructor(
    private taxReturnService: TaxReturnService,
    private config: ConfigStateService,
    private uiConfig: TenantConfigService,
    private baseUrl: BaseURLService,
    @Optional() private dialogRef?: MatDialogRef<PreviewFinalPackageComponent>,
  ) {
    this.taxReturn = this.dialogData.taxReturn;
  }

  ngOnInit() {
    this.canSendToTaxpayer = this.statusAllowSendToTaxpayer();
    this.subscriptions.push(
      this.config.loadConfig().subscribe((config) => {
        this.uiConfig = new TenantConfigService(config);
        this.taxOfficeName = this.uiConfig.config.taxOfficeName;
        this.taxpayerAlias = this.uiConfig.config.taxpayerAlias;
        this.isDocshare = this.config.tenant !== Tenant.CN && this.config.tenant !== Tenant.HK;
        this.initPreviewPackages();
      }));
  }

  createPackage() {
    this.isLoading = true;
    const requestObj = this.getFinalPackageRequestObj();
    return this.taxReturnService.createFinalPackage(this.taxReturn.id, requestObj)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(resp => {
        const sendToTaxOffice = !!requestObj.taxOfficeDocIds.length;
        this.linkUploadRetry = false;
        if (this.config.featureFlags.requireClientApproval) {
          this.successMsg = this.getFinalPackageResponseMsg(requestObj, resp);
        } else {
          const skipEmail = !!resp.skipEmail;
          const skipLink = !!resp.skipLink;
          if (resp.skipLink || sendToTaxOffice) {
            this.successMsg = 'Final package created';
          } else if (resp.uploadedToLink && !resp.skipLink) {
            this.successMsg = 'Final package created and uploaded to LINK';
          } else if (!resp.uploadedToLink && !resp.skipLink) {
            if (resp.uploadedToLink == null && resp.skipLink == null) {
              this.successMsg = this.config.featureFlags.confirmFilesSentToClientDialog? "Files sent to client have been confirmed": "No final package created.";
            } else {
              this.successMsg = "Final package created but upload to LINK failed. System will automatically retry every hour.";
            }
          }
        }
        this.modalState = 'success';
      }, (resp) => {
        if (resp.error.uploadedToLink === false) {
          if (resp.error.taxReturn && resp.error.taxReturn.docshareId) {
            this.linkUploadRetry = true;
            this.docshareId = resp.error.taxReturn.docshareId;
          }
          if (resp.error.fileSizeExceeded === true) {
            this.maxEncryptedPackageSizeExceeded = true;
          }
        }
      });
  }

  onClose(result: boolean = false) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.closed.emit();
  }

  isFormValid() {
    return !this.maxPackageSizeExceeded;
  }


  moveDocUp(index: number) {
    if (index > 0) {
      const d = this.finalPackagePreview.documents.splice(index, 1);
      this.finalPackagePreview.documents.splice(index - 1, 0, ...d);
    }
  }

  moveDocDown(index: number) {
    if (index < this.finalPackagePreview.documents.length - 1) {
      const d = this.finalPackagePreview.documents.splice(index, 1);
      this.finalPackagePreview.documents.splice(index + 1, 0, ...d);
    }
  }
  OnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  packageDocsChanged() {
    this.linkUploadRetry = false;
    setTimeout(() => {
      this.calculateTaxpayerPackageSize();
      this.checkPackageState();
      this.calculateTaxAuthPageCount();
    }, 10);
  }

  getSendToEmployerLength() {
    return this.finalPackagePreview.documents.filter(d => d.sendToEmployer).length;
  }

  getFolderDisplayName() {
    if (this.config.tenant === Tenant.AU || this.config.tenant === Tenant.ESS || this.config.tenant === Tenant.SG) {
      return "DocShare";
    }
    if (this.config.tenant === Tenant.CN || this.config.tenant === Tenant.HK) {
      return "cnDocs";
    }
    return "Folder";
  }

  onClickFolderLink(folderType: string) {
    window.open(`${this.baseUrl.get()}/tax-returns/${this.taxReturn?.id}/documents?folder=${folderType}`);
  }

  private getDocDeliveryList() {
    const deliveryList = {
      TP: [],
      ER: [],
      taxOffice: []
    };
    let tag = '';
    const deliveryTaxOfficeQuestionTag = this.uiConfig.config.deliveryTaxOfficeQuestionTag;
    this.taxReturn.questions.forEach(q => {
      tag = String(q.tag).toUpperCase();
      if (tag.startsWith('DELIVERY_')) {
        if (q.answers && q.answers[0] && q.answers[0].answer === 'true') {
          if (this.columnVisible.sendToTaxpayer && tag.endsWith('_TP')) {
            deliveryList.TP.push(q.tag.replace(/^(delivery_)/i, '').replace(/(_TP)$/i, ''));
          } else if (this.columnVisible.sendToEmployer && tag.endsWith('_ER')) {
            deliveryList.ER.push(q.tag.replace(/^(delivery_)/i, '').replace(/(_ER)$/i, ''));
          } else if (this.columnVisible.sendToTaxOffice && tag.endsWith(`_${deliveryTaxOfficeQuestionTag}`)) {
            const re = new RegExp('(_' + deliveryTaxOfficeQuestionTag + ')$', 'i');
            deliveryList.taxOffice.push(q.tag.replace(/^(delivery_)/i, '').replace(re, ''));
          }
        }
      }
    });
    return deliveryList;
  }

  private setColumnVisibility() {
    if (this.config.featureFlags.requireClientApproval) {
      this.columnVisible.sendToTaxpayer = this.canSendToTaxpayer;
      this.columnVisible.sendToEmployer = true;
      this.columnVisible.use = false;
    } else {
      this.columnVisible.sendToTaxpayer = !!this.config.featureFlags.optionSendToTaxPayer;
      this.columnVisible.sendToEmployer = !!this.config.featureFlags.optionSendToEmployer;
      this.columnVisible.use = !this.config.featureFlags.optionSendToTaxPayer && !this.config.featureFlags.optionSendToEmployer && !this.config.featureFlags.optionSendToTaxOffice;
    }
    this.modalSize = this.columnVisible.use ? 'medium' : 'large';
    this.columnVisible.fileSize = !!this.config.featureFlags.optionSendToTaxPayer;
    this.columnVisible.pageCount = !!this.config.featureFlags.showPageCountFinalPackagePreview;
    this.columnVisible.sendToTaxOffice = !!this.config.featureFlags.optionSendToTaxOffice;
    this.columnVisible.lastUpdatedBy = !!this.config.featureFlags.showLastUpdatedByFinalPackagePreview;
  }

  private initPreviewPackages() {
    this.modalState = 'loading';
    this.taxReturnService.getFinalPackagePreview(this.taxReturn.id)
      .subscribe(data => {
        this.setColumnVisibility();
        this.finalPackagePreview = this.initPreviewData(data);
        this.finalPackagePreview = {...this.finalPackagePreview, skipEmail: this.config.featureFlags.selectSkipEmailAndUnselectLinkConfirmation, skipLink: false}
        this.modalState = 'form';
        this.calculateFileSizeInKb();
        this.calculateTaxpayerPackageSize();
        this.checkPackageState();
        this.calculateTaxAuthPageCount();
      }, () => {
        this.modalState = 'error';
        this.finalPackagePreview = null;
      });
  }

  private checkPackageState() {
    if (this.finalPackagePreview.documents) {
      if (this.config.featureFlags.warnOnEmptyTaxpayerPackage) {
        this.taxpayerPackageEmpty = !this.finalPackagePreview.documents.some(d => d.sendToTaxpayer);
      }
      if (this.config.featureFlags.warnOnEmptyEmployerPackage) {
        this.employerPackageEmpty = !this.finalPackagePreview.documents.some(d => d.sendToEmployer);
      }
      if (this.config.featureFlags.warnOnEmptyTaxAuthorityPackage) {
        this.taxAuthorityPackageEmpty = !this.finalPackagePreview.documents.some(d => d.sendToTaxOffice);
      }
    }
  }

  private hideEmptyWarnings() {
    this.taxpayerPackageEmpty = false;
    this.employerPackageEmpty = false;
    this.taxAuthorityPackageEmpty = false;
  }

  private calculateFileSizeInKb() {
    if (this.finalPackagePreview.documents) {
      this.finalPackagePreview.documents.forEach(d => {
        d.sizeInKb = Math.ceil(d.size / 1024);
      })
    }
  }
  private calculateTaxpayerPackageSize() {
    this.maxEncryptedPackageSizeExceeded = false;
    let packageSize = 0;
    if (this.finalPackagePreview.documents) {
      this.finalPackagePreview.documents.forEach(d => {
        if (d.sendToTaxpayer) {
          packageSize += d.size;
        }
      })
      this.maxPackageSizeExceeded = packageSize > this.MAX_PACKAGE_SIZE;
      this.currentTaxpayerPackageSize = Math.round((packageSize / (1024 * 1024) + Number.EPSILON) * 100) / 100; // rounds to 2dp
    }
  }

  private calculateTaxAuthPageCount() {
    if (this.config.featureFlags.showPageCountFinalPackagePreview) {
      let pageCount = 0;
      if (this.finalPackagePreview.documents) {
        this.finalPackagePreview.documents.forEach(d => {
          if (d.sendToTaxOffice && d.pageCount) {
            pageCount += d.pageCount;
          }
        })
        this.taxPageTotal = pageCount;
      }
    }
  }

  private initPreviewData(data) {

    const deliveryList = this.getDocDeliveryList();
    let annexureDefaultTP = false;
    let annexureDefaultTAX = false;

    if (this.config.featureFlags.preselectTemplatesPreviewFinalPackage) {
      data.documents.map(doc => {
        // preselect annexure documents if at least 1 template document is selected for TP or TAX delivery
        // this logic only works so long as the template documents always appear before annexure documents
        if (!doc.annexure) {
          doc.sendToTaxpayer = this.defaultSendToTaxpayer(doc.name, deliveryList.TP);
          annexureDefaultTP = annexureDefaultTP || doc.sendToTaxpayer;
          doc.sendToTaxOffice = this.isDocInPreSelectList(doc.name, deliveryList.taxOffice);
          annexureDefaultTAX = annexureDefaultTAX || doc.sendToTaxOffice;
        } else {
          doc.sendToTaxpayer = annexureDefaultTP;
          doc.sendToTaxOffice = annexureDefaultTAX;
        }
        doc.sendToEmployer = this.isDocInPreSelectList(doc.name, deliveryList.ER);

      });
    }
    return data;
  }

  private defaultSendToTaxpayer(docName, preSelectList) {
    if (!this.canSendToTaxpayer) {
      return false;
    } else if (preSelectList.length > 0) {
      return this.isDocInPreSelectList(docName, preSelectList);
    }
    return false;
  }

  private isDocInPreSelectList(docName, preSelectList) {
    if (preSelectList.length > 0) {
      const docNameWithoutExt = docName.replace(/\.[^.]+$/i, '').toUpperCase();
      return !!preSelectList.find(templateType => docNameWithoutExt.endsWith(templateType.toUpperCase()));
    }
    return false;
  }

  private statusAllowSendToTaxpayer() {
    return ![
      TaxReturnStatus.TAXPAYER_SUBMITTED_EMPLOYER_FAILED.id,
      TaxReturnStatus.AMENDMENT_TAXPAYER_SUBMITTED_EMPLOYER_FAILED.id,
      TaxReturnStatus.REVISION_TAXPAYER_SUBMITTED_EMPLOYER_FAILED.id,

      TaxReturnStatus.TAXPAYER_APPROVED_EMPLOYER_FAILED.id,
      TaxReturnStatus.AMENDMENT_TAXPAYER_APPROVED_EMPLOYER_FAILED.id,
      TaxReturnStatus.REVISION_TAXPAYER_APPROVED_EMPLOYER_FAILED.id
    ].includes(this.taxReturn.status);
  }

  private getFinalPackageResponseMsg(requestObj, resp): string {
    const sendToTaxpayer = requestObj.docIds && requestObj.docIds.length > 0;
    const sendToEmployer = requestObj.employerDocIds && requestObj.employerDocIds.length > 0;
    const skipEmail = !!resp.skipEmail;
    const skipLink = !!resp.skipLink;
    let actionSuccessText = '';

    if (sendToTaxpayer && sendToEmployer) {
      if (resp.sentToEmployer === false) {
        if (skipEmail) {
          actionSuccessText = `Final package has been successfully created and submitted to ${this.taxpayerAlias}.`;
        } else {
          actionSuccessText = `Final package has been successfully created and submitted to ${this.taxpayerAlias}, but failed to send employer.`;
        }
      } else {
        const sendEmailMessage = skipEmail ? '' : 'An email has been sent to the employer.';
        actionSuccessText = resp.isKcentralClient ?
          `Final package has been successfully created and submitted to ${this.taxpayerAlias}.<br />
            ${sendEmailMessage}
            Please find employer final package <a target="_blank" href="${this.finalPackagePreview.finalFolderUrl}">here</a> and upload it to KCentral` :
          `Final package has been successfully created and submitted to ${this.taxpayerAlias} and employer.`;
      }
    } else if (sendToTaxpayer) {
      if (!resp.uploadedToLink && !resp.skipLink && this.config.featureFlags.useSpecificStatusWhenLinkUploadFails) {
        this.uploadedToLink = resp.uploadedToLink;
        actionSuccessText = 'Final package has been successfully created, however LINK upload failed. Please upload to LINK manually.';
      } else {
        actionSuccessText = `Final package has been successfully created and submitted to ${this.taxpayerAlias}.`;
      }

    } else if (sendToEmployer) {
      const sendEmailMessage = skipEmail ? '' : 'An email has been sent to the employer.';
      actionSuccessText = resp.isKcentralClient ?
        `${sendEmailMessage}
           Please find the final package <a target="_blank" href="${this.finalPackagePreview.finalFolderUrl}">here</a> and upload it to KCentral` :
        'Final package has been successfully created and submitted to employer.';
    }
    return actionSuccessText;
  }


  private getSelectedDocIds() {
    const selectedDocIds = {
      docIds: [],
      employerDocIds: [],
      taxOfficeDocIds: [],
    }
    this.finalPackagePreview.documents.map(d => {
      if (d.sendToEmployer) {
        selectedDocIds.employerDocIds.push(d.id);
      }
      if (d.sendToTaxOffice) {
        selectedDocIds.taxOfficeDocIds.push(d.id);
      }
      if (d.sendToTaxpayer && this.canSendToTaxpayer) {
        selectedDocIds.docIds.push(d.id);
      }
    });
    return selectedDocIds;
  }

  private getFinalPackageRequestObj(): PutFinalPackageBody {

    const { docIds, employerDocIds, taxOfficeDocIds } = this.getSelectedDocIds();
    const requestObj = {
      docIds: docIds,
      employerDocIds: employerDocIds,
      taxOfficeDocIds: taxOfficeDocIds,
      sendConfirmationEmail: this.sendConfirmationEmail,
      taxpayerLinkOnly: false,
      docshareId: null,
      skipLink: this.finalPackagePreview.skipLink,
      skipEmail: this.finalPackagePreview.skipEmail
    };
    if (docIds.length > 0 && this.linkUploadRetry) {
      requestObj.taxpayerLinkOnly = true;
      requestObj.docshareId = this.docshareId;
    }

    return requestObj;
  }

  private getCreatePackageButtonDisplay(): string {
    if (this.config.tenant === Tenant.FR) {
      return 'Merge and send final package';
    }
    if (this.config.featureFlags.confirmFilesSentToClientDialog) {
      return 'Confirmed';
    }
    if( this.config.featureFlags.pushDocumentsToDigitalGateway){
      return 'Yes, create package and submit documents to DG';
    }
    return 'Yes, create package and submit for approval';
  }
}
